import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowLeft, CheckCircle, CaretRight } from "@phosphor-icons/react";
import Header from "../sections/Header";
import Footer from "../sections/Footer";
import BecomePartnerDialog from "../widgets/BecomePartnerDialog";
import ConnectDialog from "../widgets/ConnectDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Empty({ message }) {
  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-24 min-h-[60vh] text-center">
      <h1 className="font-heading font-bold text-3xl md:text-4xl" style={{ color: "#0F2A6E" }}>Page not found</h1>
      <p className="mt-4 text-[#6B7A8D]">{message}</p>
      <Link to="/" className="inline-flex items-center gap-2 mt-8 font-medium" style={{ color: "#1B6FE0" }}>
        <ArrowLeft size={14} weight="bold" /> Back to home
      </Link>
    </div>
  );
}

function Bullets({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2">
      {items.map((b, i) => (
        <li key={i} className="flex items-start gap-2 text-[14.5px] text-[#1C2B3A] leading-relaxed">
          <CaretRight size={12} weight="bold" className="mt-1.5 shrink-0" style={{ color: "#1B6FE0" }} />
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}

function Subsection({ h3, body, bullets, idx }) {
  return (
    <div className="py-6 border-b border-slate-100 last:border-b-0">
      <h3 className="font-heading font-semibold text-[19px] md:text-[21px] tracking-tight" style={{ color: "#0F2A6E", letterSpacing: "-0.01em" }}>
        {h3}
      </h3>
      {body && <p className="mt-3 text-[14.5px] md:text-[14.5px] text-[#1C2B3A] leading-[1.85] font-light">{body}</p>}
      <Bullets items={bullets} />
    </div>
  );
}

function Section({ section, sectionIndex }) {
  const dark = sectionIndex % 2 === 1;
  return (
    <section className={`py-12 sm:py-16 md:py-24 ${dark ? "" : "bg-white"}`} style={dark ? { background: "#F5F7FA" } : {}}>
      <div className="max-w-5xl mx-auto px-4 lg:px-8">
        <h2 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl lg:text-[44px] tracking-tight leading-[1.1] anim anim-up"
          style={{ color: "#0F2A6E", letterSpacing: "-0.025em" }}>
          {section.h2}
        </h2>
        {section.body && (
          <p className="mt-5 sm:mt-6 text-[14px] sm:text-[14.5px] md:text-[17px] text-[#1C2B3A] leading-[1.85] font-light max-w-3xl whitespace-pre-line">
            {section.body}
          </p>
        )}
        {section.bullets && section.bullets.length > 0 && (
          <ul className="mt-6 sm:mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 stagger anim">
            {section.bullets.map((b, i) => (
              <li key={i} className="p-4 rounded-xl bg-white border border-slate-200 flex items-start gap-2.5 hover:border-[#1B6FE0]/40 hover:shadow-sm transition">
                <CheckCircle size={16} weight="duotone" className="mt-0.5 shrink-0" style={{ color: "#1B6FE0" }} />
                <span className="text-[14px] text-[#1C2B3A]">{b}</span>
              </li>
            ))}
          </ul>
        )}
        {section.subsections && section.subsections.length > 0 && (
          <div className="mt-6 divide-y divide-slate-100">
            {section.subsections.map((s, i) => (
              <Subsection key={i} h3={s.h3} body={s.body} bullets={s.bullets} idx={i} />
            ))}
          </div>
        )}
        {/* Table-style pairs (Why choose us — Advantage) */}
        {section.pairs && section.pairs.length > 0 && (
          <div className="mt-8 rounded-2xl border border-slate-200 bg-white overflow-hidden divide-y divide-slate-100">
            {section.pairs.map((p, i) => (
              <div key={i} className="grid md:grid-cols-[minmax(220px,1fr)_2fr] gap-x-8 gap-y-2 px-5 md:px-7 py-5">
                <div className="text-[13.5px] font-semibold uppercase tracking-[0.08em]" style={{ color: "#0F2A6E" }}>{p.need}</div>
                <div className="text-[14.5px] text-[#1C2B3A] font-light">{p.help}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function EditorialPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\/+|\/+$/g, "");
  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/pages/${slug}`)
      .then((r) => { if (!cancelled) setPage(r.data.page); })
      .catch(() => { if (!cancelled) setError("This page isn't published yet."); });
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (page?.seo_title) document.title = page.seo_title;
    if (page?.meta_description) {
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
      m.content = page.meta_description;
    }
  }, [page]);

  if (error) return <><Header /><Empty message={error} /><Footer /></>;
  if (!page) return <><Header /><div className="min-h-[60vh] flex items-center justify-center text-[#6B7A8D]">Loading…</div><Footer /></>;

  return (
    <>
      <Header />
      <main className="bg-white">
        {/* HERO */}
        <section className="relative overflow-hidden text-white py-14 sm:py-20 md:py-28"
          style={{ background: "linear-gradient(135deg, #0B1F4D 0%, #0F2A6E 55%, #1B6FE0 100%)" }}>
          <div className="absolute inset-0 grid-bg opacity-12" />
          <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full blur-3xl" style={{ background: "rgba(27,111,224,0.35)" }} />
          <div className="relative max-w-5xl mx-auto px-4 lg:px-8">
            <h1 className="font-heading font-bold text-3xl sm:text-4xl md:text-5xl lg:text-[60px] tracking-tight leading-[1.05]"
              style={{ letterSpacing: "-0.025em" }} data-testid="editorial-h1">
              {page.h1}
            </h1>
            {page.hero_copy && (
              <p className="mt-5 sm:mt-6 text-[14px] sm:text-[14.5px] md:text-[17px] text-slate-200/90 leading-relaxed font-light max-w-3xl whitespace-pre-line">
                {page.hero_copy}
              </p>
            )}
          </div>
        </section>

        {/* SECTIONS */}
        {(page.sections || []).map((s, i) => (
          <Section key={i} section={s} sectionIndex={i} />
        ))}

        {/* FEATURED SNIPPET */}
        {page.snippet && (
          <section className="py-12 md:py-16 bg-white border-t border-slate-100">
            <div className="max-w-5xl mx-auto px-4 lg:px-8">
              <div className="rounded-2xl p-6 md:p-8 border border-slate-200 bg-slate-50/60">
                <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold" style={{ color: "#1B6FE0" }}>In one line</div>
                <p className="mt-2 text-[15px] md:text-[15.5px] text-[#1C2B3A] leading-relaxed font-light">{page.snippet}</p>
              </div>
            </div>
          </section>
        )}

        {/* FINAL CTA */}
        <section id="editorial-cta" className="py-14 sm:py-20 md:py-24 relative overflow-hidden text-white"
          style={{ background: "linear-gradient(135deg, #0F2A6E 0%, #1B6FE0 100%)" }}>
          <div className="absolute inset-0 grid-bg opacity-12" />
          <div className="relative max-w-4xl mx-auto px-4 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl lg:text-[44px] tracking-tight leading-[1.1] anim anim-up"
              style={{ letterSpacing: "-0.025em" }}>
              {page.cta_heading || "Ready to get started?"}
            </h2>
            {page.cta_body && (
              <p className="mt-5 text-[14px] sm:text-[14.5px] md:text-[17px] text-slate-200/90 font-light leading-relaxed max-w-2xl mx-auto">{page.cta_body}</p>
            )}
            <div className="mt-8 sm:mt-9 flex flex-wrap justify-center gap-3">
              <button type="button" onClick={() => setConnectOpen(true)} data-testid="editorial-cta-primary"
                className="inline-flex items-center gap-2 h-11 sm:h-12 px-5 sm:px-7 rounded-md bg-white text-[#0F2A6E] font-semibold hover:bg-blue-50">
                {page.cta_text || "Contact us"} <ArrowRight size={15} weight="bold" />
              </button>
              <button onClick={() => setPartnerOpen(true)} data-testid="editorial-cta-partner"
                className="inline-flex items-center gap-2 h-11 sm:h-12 px-5 sm:px-7 rounded-md border border-white/40 text-white font-medium hover:bg-white/10">
                Become a partner
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BecomePartnerDialog open={partnerOpen} onOpenChange={setPartnerOpen} />
      <ConnectDialog open={connectOpen} onOpenChange={setConnectOpen} />
    </>
  );
}
